'use client'

import { createRef, useEffect } from 'react'
import { MdInfoOutline, MdPriceCheck } from 'react-icons/md'
import type { OrderSettlement } from '@/lib/api/orders/order-api'

interface SettleOrderModalProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    /** `null` enquanto carrega os valores do backend. */
    settlement: OrderSettlement | null
    loadingPreview?: boolean
    loading?: boolean
}

const money = (centavos: number) =>
    (centavos / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })

/**
 * Confirmação da quitação com desconto.
 *
 * Não é um "tem certeza?": quem confirma está liberando repasse de dinheiro que o
 * cliente não pagou por inteiro, e a diferença vira desconto no procedimento. Os
 * três números — combinado, pago, desconto — ficam na cara antes do clique, junto
 * com o total de repasse que sai, porque com desconto grande ele pode passar do
 * que entrou.
 *
 * O aviso do "pagou por outro meio" está aqui de propósito: o erro caro não é
 * quitar quando não devia, é quitar um valor que o paciente pagou em dinheiro e
 * ninguém registrou — aí o desconto é fictício e o faturamento encolhe sozinho.
 */
export default function SettleOrderModal({
    isOpen,
    onClose,
    onConfirm,
    settlement,
    loadingPreview = false,
    loading = false,
}: SettleOrderModalProps) {
    const modalRef = createRef<HTMLDialogElement>()

    useEffect(() => {
        if (isOpen) modalRef.current?.showModal()
        else modalRef.current?.close()
    }, [isOpen])

    const blocked = !!settlement && !settlement.canSettle
    const pending = settlement?.pendingRequestId ?? null
    // Repasse maior que o recebido: a clínica paga para atender. Não bloqueia — é
    // decisão de quem aprova —, mas não pode passar despercebido.
    const negativeMargin =
        !!settlement && settlement.payoutAmount > settlement.paidAmount

    return (
        <dialog ref={modalRef} className="modal" onClose={onClose}>
            <div className="modal-box max-w-lg !rounded-2xl">
                <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-warning/15 text-warning">
                        <MdPriceCheck size={22} />
                    </span>
                    <div className="min-w-0">
                        <h3 className="text-lg font-semibold">
                            Marcar como quitado
                        </h3>
                        <p className="mt-1 text-sm text-base-content/70">
                            A diferença entre o combinado e o pago será lançada
                            como <strong>desconto</strong>, e os repasses serão
                            liberados pelo valor cheio.
                        </p>
                    </div>
                </div>

                {loadingPreview && (
                    <div className="py-10 text-center">
                        <span className="loading loading-spinner loading-md text-secondary" />
                    </div>
                )}

                {!loadingPreview && settlement && (
                    <>
                        <div className="mt-5 space-y-2 rounded-xl bg-base-200/60 p-4 text-sm">
                            <div className="flex items-center justify-between">
                                <span className="text-base-content/70">
                                    Valor combinado
                                </span>
                                <span className="font-medium">
                                    {money(settlement.agreedAmount)}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-base-content/70">
                                    Recebido do paciente
                                </span>
                                <span className="font-medium">
                                    {money(settlement.paidAmount)}
                                </span>
                            </div>
                            <div className="flex items-center justify-between border-t border-base-300 pt-2">
                                <span className="font-medium text-warning">
                                    Vira desconto
                                </span>
                                <span className="font-semibold text-warning">
                                    {money(settlement.discountAmount)}
                                </span>
                            </div>
                            <div className="flex items-center justify-between border-t border-base-300 pt-2">
                                <span className="text-base-content/70">
                                    Repasses a liberar
                                </span>
                                <span
                                    className={`font-semibold ${negativeMargin ? 'text-error' : ''}`}
                                >
                                    {money(settlement.payoutAmount)}
                                </span>
                            </div>
                        </div>

                        {negativeMargin && (
                            <p className="mt-3 text-sm text-error">
                                Os repasses somam mais do que entrou de
                                pagamento. A clínica arca com a diferença.
                            </p>
                        )}

                        <div className="mt-4 flex gap-2 rounded-xl border border-info/30 bg-info/10 p-3 text-sm">
                            <MdInfoOutline
                                size={18}
                                className="mt-0.5 shrink-0 text-info"
                            />
                            <p className="text-base-content/80">
                                Se o paciente pagou o restante por outro meio,
                                não use esta ação: gere a cobrança pela opção{' '}
                                <strong>Gerar cobrança</strong> e registre o
                                pagamento com a forma <strong>Outro</strong>.
                                Assim o valor entra no faturamento em vez de
                                virar desconto.
                            </p>
                        </div>

                        {blocked && (
                            <p className="mt-4 text-sm text-error">
                                {settlement.reason}
                            </p>
                        )}

                        {pending && (
                            <p className="mt-4 text-sm text-warning">
                                Já existe uma solicitação aguardando aprovação
                                (#{pending}).
                            </p>
                        )}

                        <p className="mt-4 text-xs text-base-content/60">
                            A quitação não acontece agora: ela vai para as
                            aprovações financeiras e precisa ser aprovada por
                            outra pessoa.
                        </p>
                    </>
                )}

                <div className="modal-action">
                    <button
                        type="button"
                        className="btn btn-ghost !rounded-lg"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-warning !border-0 !rounded-lg"
                        onClick={onConfirm}
                        disabled={
                            loading || loadingPreview || blocked || !!pending
                        }
                    >
                        {loading && (
                            <span className="loading loading-spinner loading-xs" />
                        )}
                        Solicitar quitação
                    </button>
                </div>
            </div>
            <form method="dialog" className="modal-backdrop">
                <button type="button" onClick={onClose}>
                    fechar
                </button>
            </form>
        </dialog>
    )
}
